// Raise a one-off draft invoice (Session AQF, Director decision: on the Invoices page).
//
// A one-off draft bills a customer a single line with no quote, contract, call-out or project behind
// it. It is what Session APL's refusal points the office at when a quote can never become a service
// agreement (the D60 no-agreement quote rule), and what the office needs for any other ad-hoc charge.
//
// It calls POST /api/invoices, which has existed since Session AGE and is the same route the Guvnor
// tool create_draft_invoice dispatches: ONE invoice path, never a second one. Everything material is
// the route's decision, not this modal's -- it resolves the Xero contact, applies 30-day terms when
// no due date is given, rounds the money to whole pence, mirrors the draft into the cache and writes
// the audit row. This modal validates the same shapes first so a slip is caught before Xero is
// touched, and it names the customer and the figure before it writes anything (the branded-confirm
// rule for a consequential action, Session AEH).
//
// DRAFT ONLY. The route hardcodes Status "DRAFT" and never authorises, so nothing reaches the
// customer from here. THERE IS DELIBERATELY NO VOID IN THE PLATFORM (Director decision, Session
// AQF): a mistaken draft is voided in Xero, which is the arbiter, and the mirrored InvoiceCache row
// is never deletable. The confirm step says exactly that, so nobody presses it expecting an undo
// button on this page.
import { Button } from "../../components/Button";
import { formatDate } from "../../lib/format";
import { useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { OneOffDraftInvoiceResult } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { RecordPicker } from "../../components/RecordPicker";
import { XERO_SALES_ACCOUNT_OPTIONS, xeroSalesAccountLabel } from "../../lib/xeroSalesAccounts";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const hintCls = "mt-1 text-xs text-ele-body opacity-80";

// The route's own limits (server/src/modules/invoices/invoice.routes.ts createDraftBody), mirrored
// here so the office is told plainly instead of being handed a 400.
const MAX_DESCRIPTION = 500;
const MAX_REFERENCE = 100;
const MAX_AMOUNT = 1_000_000;

function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function OneOffDraftInvoiceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: OneOffDraftInvoiceResult) => void;
}) {

  const [step, setStep] = useState<"form" | "confirm">("form");
  const [customerId, setCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [description, setDescription] = useState("");
  const [amountText, setAmountText] = useState("");
  const [accountCode, setAccountCode] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const amount = Math.round(Number(amountText) * 100) / 100;

  function review() {
    setError(null);
    if (!customerId) return setError("Choose the customer this invoice is for.");
    if (description.trim().length === 0) return setError("Say what the invoice is for.");
    if (description.trim().length > MAX_DESCRIPTION) return setError(`Keep the description to ${MAX_DESCRIPTION} characters or fewer.`);
    if (!Number.isFinite(Number(amountText)) || amountText.trim() === "") return setError("Enter the amount as a number, for example 450.00");
    if (amount <= 0) return setError("The amount must be more than zero.");
    if (amount > MAX_AMOUNT) return setError(`The amount must be ${gbp(MAX_AMOUNT)} or less.`);
    if (!accountCode) return setError("Choose the Xero account this should be billed to.");
    if (dueDate && Number.isNaN(new Date(dueDate).getTime())) return setError("That due date is not a date.");
    if (reference.trim().length > MAX_REFERENCE) return setError(`Keep the reference to ${MAX_REFERENCE} characters or fewer.`);
    setStep("confirm");
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.invoices.createDraft({
        customerId,
        description: description.trim(),
        amountExVat: amount,
        accountCode,
        ...(dueDate ? { dueDate } : {}),
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      });
      onCreated(result);
    } catch (err) {
      // The route's own refusals, each said in the office's words. A 502 is the one worth spelling
      // out: Xero rejected it, so nothing was raised and the office has somewhere to look.
      const msg =
        err instanceof ApiError
          ? err.status === 503
            ? "Xero is not connected, so this invoice cannot be created yet. Connect Xero in Settings and try again."
            : err.status === 502
              ? "Xero would not create this invoice. Check the customer's name and email in Xero, and that the account is a sales account."
              : err.status === 404
                ? "That customer could not be found. Refresh the page and try again."
                : err.status === 400
                  ? "Xero could not accept those details. Check the amount and the due date."
                  : "Could not create this draft. Please try again in a moment."
          : "Could not create this draft. Please try again in a moment.";
      setError(msg);
      setStep("form");
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      ariaLabel="Raise a one-off draft invoice"
      onClose={() => !busy && onClose()}
      width="max-w-[560px]"
      panelClassName="p-6"
    >
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Raise a one-off draft invoice</h2>
      <p className="mb-4 text-sm text-ele-body">
        For a charge with no quote, contract, call-out or project behind it. It is created as a draft in Xero: nothing is
        sent to the customer and nothing is authorised.
      </p>

      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
          {error}
        </p>
      )}

      {step === "form" ? (
        <div className="flex flex-col gap-3">
          <div>
            <RecordPicker
              kind="customer"
              id="one-off-customer"
              label="Customer"
              required
              value={customerId}
              placeholder="Search for a customer"
              onChange={(id, rec) => {
                setCustomerId(id);
                setCustomerName(rec?.label ?? "");
              }}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="one-off-description">
              What is it for?
            </label>
            <input
              id="one-off-description"
              type="text"
              maxLength={MAX_DESCRIPTION}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={inputCls}
              placeholder="Replacement door lock, Rosina Court"
            />
            <p className={hintCls}>This is the line the customer reads on the invoice.</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="one-off-amount">
                Amount ex VAT
              </label>
              <input
                id="one-off-amount"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                className={inputCls}
                placeholder="450.00"
              />
              <p className={hintCls}>Xero adds VAT on top at the account's rate.</p>
            </div>
            <div>
              <label className={labelCls} htmlFor="one-off-account">
                Bill it to
              </label>
              <select id="one-off-account" value={accountCode} onChange={(e) => setAccountCode(e.target.value)} className={inputCls}>
                <option value="">Choose an account</option>
                {XERO_SALES_ACCOUNT_OPTIONS.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.label} ({a.code})
                  </option>
                ))}
              </select>
              <p className={hintCls}>The Xero sales account this revenue lands on.</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="one-off-due">
                Due date
              </label>
              <input id="one-off-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
              <p className={hintCls}>Leave it blank for 30 day terms.</p>
            </div>
            <div>
              <label className={labelCls} htmlFor="one-off-reference">
                Reference
              </label>
              <input
                id="one-off-reference"
                type="text"
                maxLength={MAX_REFERENCE}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className={inputCls}
                placeholder="Optional"
              />
              <p className={hintCls}>Your own reference, if the customer needs one.</p>
            </div>
          </div>

          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <Button
              onClick={review}
              className={MODAL_ACTION_BTN}
            >
              Continue
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="mb-3 text-sm text-ele-body">Check this before it goes to Xero.</p>
          <dl className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm">
            <div className="flex justify-between gap-4 py-1">
              <dt className="text-ele-body">Customer</dt>
              <dd className="text-right text-ele-strong">{customerName}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1">
              <dt className="text-ele-body">For</dt>
              <dd className="text-right text-ele-strong">{description.trim()}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1">
              <dt className="text-ele-body">Amount ex VAT</dt>
              <dd className="text-right text-ele-strong">{gbp(amount)}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1">
              <dt className="text-ele-body">Billed to</dt>
              <dd className="text-right text-ele-strong">{xeroSalesAccountLabel(accountCode)}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1">
              <dt className="text-ele-body">Due</dt>
              <dd className="text-right text-ele-strong">{dueDate ? formatDate(dueDate) : "30 days from today"}</dd>
            </div>
            {reference.trim() && (
              <div className="flex justify-between gap-4 py-1">
                <dt className="text-ele-body">Reference</dt>
                <dd className="text-right text-ele-strong">{reference.trim()}</dd>
              </div>
            )}
          </dl>
          <p className="mb-4 text-sm text-ele-body">
            This creates a draft in Xero for {customerName || "this customer"}. It is not sent and it is not authorised.
            If it is wrong, void it in Xero: drafts cannot be voided or deleted from this page.
          </p>
          <div className={`gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={() => setStep("form")}
              disabled={busy}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Back
            </button>
            <Button
              onClick={create}
              disabled={busy}
              className={MODAL_ACTION_BTN}
            >
              {busy ? "Creating..." : "Raise the draft"}
            </Button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
